import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getStoreStats, StoreStats, StoreStatsSummary } from '../services/adminApiService.ts';
import './StoreStatsTable.css';

interface StoreStatsTableProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

const StoreStatsTable: React.FC<StoreStatsTableProps> = ({
  autoRefresh = true,
  refreshInterval = 300000 // 5 minutes
}) => {
  const [stats, setStats] = useState<StoreStats[]>([]);
  const [summary, setSummary] = useState<StoreStatsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchStoreStats = async () => {
    try {
      setError(null);
      const auth = getAuth();
      if (!auth.currentUser) {
        throw new Error('Not authenticated');
      }
      const token = await auth.currentUser.getIdToken();
      const response = await getStoreStats(token);
      setStats(response.stores);
      setSummary(response.summary);
      setLastUpdated(response.timestamp);
    } catch (err) {
      console.error('Error fetching store stats:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch menu statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoreStats();
    
    if (autoRefresh) {
      const interval = setInterval(fetchStoreStats, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const formatLastUpdated = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatRelativeTime = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Generate QA site URL for a store
  const generateQaUrl = (store: StoreStats) => {
    // Map legacy IDs to store info for URL generation
    const storeUrlMap: Record<string, { state: string; city: string }> = {
      '857': { state: 'maryland', city: 'olney' },
      '5440': { state: 'maryland', city: 'olney' },
      '6158': { state: 'connecticut', city: 'stamford' },
      '6159': { state: 'connecticut', city: 'stamford' },
      '5796': { state: 'connecticut', city: 'west-hartford' },
      '5797': { state: 'connecticut', city: 'west-hartford' },
      '4855': { state: 'new-jersey', city: 'voorhees' },
      '5377': { state: 'new-jersey', city: 'voorhees' },
      '696002abeb54a2091b709e0f': { state: 'new-jersey', city: 'hamilton' },
      '4545': { state: 'rhode-island', city: 'exeter' },
      '4996': { state: 'rhode-island', city: 'exeter' },
      '6145': { state: 'vermont', city: 'essex-junction' }
    };
    
    const storeInfo = storeUrlMap[store.storeId];
    if (!storeInfo) return 'https://sweetspot-qa.web.app/'; // fallback
    
    const menuType = store.storeType === 'medical' ? 'dispensary-medical-menu' : 'dispensary-recreational-menu';
    return `https://sweetspot-qa.web.app/store/${storeInfo.state}/${storeInfo.city}/${menuType}`;
  };

  // Group stats by state
  const statsByState = stats.reduce((acc, store) => {
    if (!acc[store.state]) {
      acc[store.state] = [];
    }
    acc[store.state].push(store);
    return acc;
  }, {} as Record<string, StoreStats[]>);

  // Sort states alphabetically and stores within each state
  Object.keys(statsByState).forEach(state => {
    statsByState[state].sort((a, b) => {
      // First sort by store name
      const storeCompare = a.storeName.localeCompare(b.storeName);
      if (storeCompare !== 0) return storeCompare;
      // Then by type (medical first)
      return a.storeType === 'medical' ? -1 : 1;
    });
  });

  if (loading) {
    return (
      <div className="store-stats-loading">
        <div className="loading-spinner"></div>
        <span>Loading menu statistics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="store-stats-error">
        <span className="error-icon">⚠️</span>
        <div className="error-content">
          <div className="error-title">Unable to load menu statistics</div>
          <div className="error-message">{error}</div>
          <button 
            className="retry-button"
            onClick={fetchStoreStats}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="store-stats-table-container">
      <div className="stats-header">
        <div className="stats-title">
          <span className="title-icon">📊</span>
          Menu Statistics
        </div>
        
        {summary && (
          <div className="stats-summary">
            <span className="summary-total">
              {summary.totalProducts.toLocaleString()} total products across {summary.totalStores} menus
            </span>
          </div>
        )}
        
        {lastUpdated && (
          <div className="last-updated">
            Last updated: {formatLastUpdated(lastUpdated)}
          </div>
        )}
      </div>

      <div className="table-container">
        <table className="stats-table">
          <thead>
            <tr>
              <th>QA</th>
              <th>Menu</th>
              <th>Products</th>
              <th>Last Updated</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(statsByState)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([state, stores]) => 
                stores.map((store, index) => {
                  // Format menu name: CITY, STATE MED/REC
                  const cityName = store.storeName
                    .replace(' Medical', '')
                    .replace(' Recreational', '')
                    .toUpperCase();
                  const menuType = store.storeType === 'medical' ? 'MED' : 'REC';
                  const menuName = `${cityName}, ${store.state} ${menuType}`;
                  
                  return (
                    <tr key={store.storeId} className={`store-row ${store.status}`}>
                      <td className="qa-cell">
                        <a 
                          href={generateQaUrl(store)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="qa-link"
                          title={`Visit QA site for ${menuName}`}
                        >
                          🛰️
                        </a>
                      </td>
                      <td className="menu-name">{menuName}</td>
                      <td className="product-count">
                        {store.status === 'healthy' ? (
                          <span className="count-number">{store.productCount.toLocaleString()}</span>
                        ) : (
                          <span className="count-error" title={store.error}>Error</span>
                        )}
                      </td>
                      <td className="last-updated-cell">
                        <span 
                          className="last-updated-time" 
                          title={new Date(store.lastUpdated).toLocaleString()}
                        >
                          {formatRelativeTime(store.lastUpdated)}
                        </span>
                      </td>
                      <td className="status-cell">
                        <span className={`status-indicator ${store.status === 'healthy' && store.productCount > 0 ? 'healthy' : 'error'}`}>
                          {store.status === 'healthy' && store.productCount > 0 ? '✅' : '❌'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
          </tbody>
        </table>
      </div>
      
      <div className="stats-actions">
        <button 
          className="refresh-button"
          onClick={fetchStoreStats}
          disabled={loading}
        >
          🔄 Refresh Stats
        </button>
      </div>
    </div>
  );
};

export default StoreStatsTable;